export function ProfileFieldGrid({ children, columns = 2 }) {
  return (
    <div className={`ep-field-grid ep-field-grid--${columns}`}>
      {children}
    </div>
  );
}

export function ProfileField({ label, value, mono = false, highlight = false, icon: Icon, tone }) {
  const display = value === "" || value == null ? "—" : value;
  if (Icon) {
    return (
      <div className={`ep-field ep-field--rich ep-ftone--${tone || "indigo"}`}>
        <span className="ep-field-chip" aria-hidden>
          <Icon size={17} strokeWidth={2} />
        </span>
        <span className="ep-field-text">
          <span className="ep-field-label">{label}</span>
          <span className={`ep-field-value${mono ? " ep-mono" : ""}`}>{display}</span>
        </span>
      </div>
    );
  }
  return (
    <div className={`ep-field${highlight ? " ep-field--highlight" : ""}`}>
      <span className="ep-field-label">{label}</span>
      <span className={`ep-field-value${mono ? " ep-mono" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

export function ProfilePanel({ title, subtitle, actions, children, icon: Icon, tone, variant = "default" }) {
  return (
    <section className={`ep-panel ep-panel--${variant}${tone ? ` ep-tone--${tone}` : ""}`}>
      {(title || actions) && (
        <header className="ep-panel-head">
          {Icon ? (
            <span className="ep-panel-icon" aria-hidden>
              <Icon size={18} strokeWidth={2} />
            </span>
          ) : null}
          <div className="ep-panel-head-text">
            {title ? <h3 className="ep-panel-title">{title}</h3> : null}
            {subtitle ? <p className="ep-panel-sub">{subtitle}</p> : null}
          </div>
          {actions ? <div className="ep-panel-actions">{actions}</div> : null}
        </header>
      )}
      <div className="ep-panel-body">{children}</div>
    </section>
  );
}

/**
 * Label / value table laid out as `pairs` label-value pairs per row.
 * rows: [{ label, value, mono? }]
 */
export function ProfileDetailTable({ rows, pairs = 2, caption, headers = ["Field", "Details"], className = "" }) {
  const chunks = [];
  for (let i = 0; i < rows.length; i += pairs) chunks.push(rows.slice(i, i + pairs));
  return (
    <div className={`ep-kv-wrap${className ? ` ${className}` : ""}`}>
      <table className={`ep-kv-table${pairs === 1 ? " ep-kv-table--single" : ""}`}>
        {caption ? <caption className="ep-sr-only">{caption}</caption> : null}
        {pairs === 1 && headers ? (
          <thead>
            <tr>
              <th scope="col">{headers[0]}</th>
              <th scope="col">{headers[1]}</th>
            </tr>
          </thead>
        ) : null}
        <tbody>
          {chunks.map((chunk, rowIdx) => (
            <tr key={rowIdx}>
              {Array.from({ length: pairs }).map((_, i) => {
                const f = chunk[i];
                if (!f) {
                  return [
                    <th key={`eh${i}`} className="ep-kv-empty" aria-hidden />,
                    <td key={`ed${i}`} className="ep-kv-empty" aria-hidden />,
                  ];
                }
                const value = f.value === "" || f.value == null ? "—" : f.value;
                return [
                  <th key={`h${i}`} scope="row">{f.label}</th>,
                  <td key={`d${i}`} className={f.mono ? "ep-mono" : undefined}>{value}</td>,
                ];
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProfileSkeleton({ rows = 4 }) {
  return (
    <div className="ep-skeleton-wrap" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="ep-skeleton-line" style={{ width: `${70 + (i % 3) * 10}%` }} />
      ))}
    </div>
  );
}

export function ProfileEmptyState({ title, description, action }) {
  return (
    <div className="ep-empty">
      <p className="ep-empty-title">{title}</p>
      {description ? <p className="ep-empty-desc">{description}</p> : null}
      {action}
    </div>
  );
}

export function ProfileErrorState({ message, onRetry }) {
  return (
    <div className="ep-error">
      <p>{message || "Unable to load this section."}</p>
      {onRetry ? (
        <button type="button" className="ep-btn ep-btn-secondary" onClick={onRetry}>
          Retry
        </button>
      ) : null}
    </div>
  );
}

export function ProfileTableWrap({ children }) {
  return <div className="ep-table-wrap">{children}</div>;
}
